import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, List, ListItem, ListItemButton, ListItemText, Collapse, TextField, CircularProgress, Box, IconButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useDeptTreeList, Dept } from '@/services/manageService/deptService';
import { useTheme } from '@mui/material/styles';

interface UserListAsideProps {
    onDeptSelect: (deptId: number | string) => void;
}

const UserListAside = ({ onDeptSelect }: UserListAsideProps) => {
    const theme = useTheme();
    const [tree, setTree] = useState<Dept[]>([]);
    const [loading, setLoading] = useState(true);
    const [openMap, setOpenMap] = useState<Record<number, boolean>>({});
    const [search, setSearch] = useState('');

    useEffect(() => {
        setLoading(true);
        useDeptTreeList().then(res => {
            // Add 'All' option at the beginning
            const allOption: Dept = {
                deptId: 0,
                pid: 0,
                companyName: '',
                name: 'All Dept',
                num: 0,
                remark: '',
                deptHeadId: 0,
                deptHeadName: '',
                currentNum: 0
            };
            setTree([allOption, ...res]);
        }).finally(() => setLoading(false));
    }, []);

    const onToggle = useCallback((id: number) => {
        setOpenMap(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const filteredTree = filterDeptTree(tree, search);

    // Function to collect all node IDs for default expansion
    const getAllNodeIds = (nodes: Dept[]): string[] => {
        return nodes.reduce((acc: string[], node) => {
            acc.push(node.deptId.toString());
            if (node.children) {
                acc.push(...getAllNodeIds(node.children));
            }
            return acc;
        }, []);
    };

    const handleNodeSelect = (event: React.SyntheticEvent, nodeId: string) => {
        if (nodeId === '') {
            onDeptSelect(nodeId);
        } else {
            const deptId = parseInt(nodeId, 10);
            onDeptSelect(deptId);
        }
    };

    return (
        <Card
            sx={{
                display: { xs: 'none', md: 'block' },
                order: -1,
                flex: '0 0 15em',
                mr: 2,
                mt: 6,
                alignSelf: 'flex-start',
                boxShadow: theme.shadows[1],
                background: theme.palette.background.paper
            }}
        >
            <CardContent sx={{ pt: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search Dept"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ mb: 2, background: theme.palette.action.hover }}
                />
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <List disablePadding>
                        {filteredTree.map(node => (
                            <AsideDeptNode key={node.deptId} node={node} openMap={openMap} onToggle={onToggle} theme={theme} onNodeSelect={handleNodeSelect} />
                        ))}
                    </List>
                )}
            </CardContent>
        </Card>
    );
};

interface AsideDeptNodeProps {
    node: Dept;
    openMap: Record<number, boolean>;
    onToggle: (id: number) => void;
    theme: any;
    onNodeSelect: (event: React.SyntheticEvent, nodeId: string) => void;
}

const AsideDeptNode: React.FC<AsideDeptNodeProps> = ({ node, openMap, onToggle, theme, onNodeSelect }) => {
    const hasChildren = node.children && node.children.length > 0;
    const open = openMap[node.deptId] || false;

    return (
        <>
            <ListItem 
                dense 
                disablePadding
                sx={{ mb: 0.5 }}
            >
                <ListItemButton
                    dense
                    onClick={(event) => onNodeSelect(event, node.deptId.toString())}
                    sx={{
                        pl: 2,
                        borderRadius: 1,
                        '&.Mui-selected': {
                            backgroundColor: theme.palette.action.selected,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        },
                    }}
                >
                    <ListItemText primary={node.name} />
                </ListItemButton>
                {hasChildren && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle(node.deptId);
                        }}
                        sx={{ mr: 1 }}
                    >
                        {open ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </ListItem>
            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                        {node.children!.map(child => (
                            <AsideDeptNode 
                                key={child.deptId} 
                                node={child} 
                                openMap={openMap} 
                                onToggle={onToggle} 
                                theme={theme} 
                                onNodeSelect={onNodeSelect} 
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

function filterDeptTree(tree: Dept[], keyword: string): Dept[] {
    if (!keyword) return tree;
    const match = (node: Dept): boolean => node.name.includes(keyword);
    const dfs = (nodes: Dept[]): Dept[] => {
        return nodes.map(node => {
            const children = node.children ? dfs(node.children) : [];
            if (match(node) || children.length > 0) {
                return { ...node, children };
            }
            return null;
        }).filter(Boolean) as Dept[];
    };
    return dfs(tree);
}

export default UserListAside;
